import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, Truck, MapPin } from "lucide-react";
import { CONTACT, IMAGES } from "@/data";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const contentOpacity = useTransform(scrollY, [0, 450], [1, 0.15]);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${IMAGES.hero})`, y: bgY }}
        initial={{ scale: 1.22 }}
        animate={{ scale: 1.12 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0C1827]/95 via-[#0C1827]/70 to-[#0C1827]/25" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B131F] to-transparent" />

      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="mist-layer mist-a" />
        <div className="mist-layer mist-b" />
      </div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full"
      >
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="inline-flex items-center gap-2 border border-[#F5A623]/40 bg-[#F5A623]/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-8"
          >
            <MapPin size={14} className="text-[#F5A623]" />
            <span className="font-mono-accent text-xs uppercase tracking-[0.2em] text-[#F5A623]">
              Almaguin Highlands &bull; Muskoka
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-white uppercase"
          >
            <MaskedLine delay={0.35}>Premier Excavation &amp;</MaskedLine>
            <MaskedLine delay={0.5}>Landscaping in</MaskedLine>
            <MaskedLine delay={0.65} className="text-[#F5A623]">
              Cottage Country
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
          >
            Transforming Almaguin and Muskoka terrain with precision machinery, armour stone,
            hydroseeding, and site mastery — from raw bush lot to finished yard.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <motion.a
              href="#quote"
              data-testid="hero-cta-quote"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#0C1827] font-display font-bold text-base px-8 py-4 rounded-full shadow-lg shadow-[#F5A623]/25"
            >
              Request Multi-Service Quote
            </motion.a>
            <motion.a
              href={CONTACT.phoneHref}
              data-testid="hero-cta-call"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-display font-bold text-base px-8 py-4 rounded-full backdrop-blur-sm"
            >
              <Phone size={18} className="text-[#F5A623]" />
              Call {CONTACT.phone}
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.45 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          >
            {[
              { icon: ShieldCheck, label: "Fully Insured" },
              { icon: Truck, label: "Commercial Grade Fleet" },
              { icon: MapPin, label: "Local Almaguin Craftsmen" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-sm text-slate-300">
                <Icon size={16} className="text-[#F5A623]" />
                {label}
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        aria-hidden="true"
      >
        <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-slate-400">
          Scroll
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#F5A623] to-transparent"
        />
      </motion.div>
    </section>
  );
};
